import { Link } from "react-router-dom";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { getStepPath } from "@/lib/campaigns";
import { isAnonymousLinks } from "@/utils/campaignMode-helper";

/**
 * The inline "Add recipients to continue" prompt above the disabled CTA.
 * Mirrors the prototype's `.checkout-no-recipients` / `#noRecipientsInline`
 * (Campaign-Builder-Master-Prototype-Stage2-Revisions.html, markup ~line 11069,
 * CSS ~line 8026, `updateNoRecipientsTip()` ~line 24088).
 *
 * The Stage 2 revisions retired the floating, dismissible `#noRecipientsTip`
 * popover in favour of this one centred wine line — "Phase 40 — the floating
 * popup was replaced by an inline message" (~line 24089). Nothing is
 * dismissible any more, so there is no local state: the line is present
 * exactly while the recipient list is empty, which also makes it a stable
 * `aria-describedby` target for the inert CTA below it.
 */
export interface NoRecipientsTipProps {
  /** Pointed at by the CTA's `aria-describedby` — this line IS the reason it is inert. */
  id?: string;
  className?: string;
}

export default function NoRecipientsTip({ id, className }: NoRecipientsTipProps) {
  const { recipients, notificationMode, linkToList } = useCampaignBuilder();

  // Anonymous-links campaigns (manual Path A, or any universal) have no recipient list by design — the
  // link/redemption quantity is the count instead — so the prompt must never show there. Manual Path B
  // (link to a list) DOES need recipients, exactly like gfg, so the prompt shows there when empty.
  const anonymousLinks = isAnonymousLinks(notificationMode, linkToList);
  if (anonymousLinks || recipients.length > 0) return null;

  return (
    <p
      id={id}
      className={[
        "mt-4 mb-0 text-center font-lato text-[13.5px] font-semibold text-good-wine",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <Link
        to={getStepPath(2)}
        className="text-good-wine underline underline-offset-2 hover:text-good-wine-2"
      >
        Add recipients
      </Link>{" "}
      to continue
    </p>
  );
}
